import { useCallback } from "react";

import type { HotelInfoItemInput } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "../../_model/types/auto-generator-for-view-model";

type Props = {
	hotelInfo: ForArrayType<HotelInfoItemInput[]>;
	onSetViewModel: <K extends "hotelInfo">(values: { key: K; value: ForArrayType<HotelInfoItemInput[]> }[]) => void;
};

export const useHotelInfo = ({ hotelInfo, onSetViewModel }: Props) => {
	const append = useCallback(() => {
		const newHotelInfoItem = {
			title: {
				isEdited: true,
				value: "",
			},
			description: {
				isEdited: true,
				value: "",
			},
		};

		const newHotelInfo = {
			...hotelInfo,
			value: [...hotelInfo.value, newHotelInfoItem],
		} satisfies ForArrayType<HotelInfoItemInput[]>;

		onSetViewModel([{ key: "hotelInfo", value: newHotelInfo }]);
	}, [hotelInfo, onSetViewModel]);

	const removeHotelInfoByIndex = useCallback(
		(index: number) => {
			const newHotelInfo = {
				...hotelInfo,
				value: hotelInfo.value.filter((_, i) => i !== index),
			};

			onSetViewModel([{ key: "hotelInfo", value: newHotelInfo }]);
		},
		[hotelInfo, onSetViewModel],
	);

	const updateTitle = useCallback(
		(value: string, index: number) => {
			const newHotelInfo = {
				...hotelInfo,
				value: hotelInfo.value.map((item, i) => {
					if (i === index) {
						return {
							...item,
							title: {
								isEdited: true,
								value,
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "hotelInfo", value: newHotelInfo }]);
		},
		[hotelInfo, onSetViewModel],
	);

	const updateDescription = useCallback(
		(value: string, index: number) => {
			const newHotelInfo = {
				...hotelInfo,
				value: hotelInfo.value.map((item, i) => {
					if (i === index) {
						return {
							...item,
							description: {
								isEdited: true,
								value,
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "hotelInfo", value: newHotelInfo }]);
		},
		[hotelInfo, onSetViewModel],
	);

	const reorderHotelInfo = useCallback(
		(activeIndex: number, overIndex: number) => {
			if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
				return;
			}

			const next = [...hotelInfo.value];
			const [removed] = next.splice(activeIndex, 1);
			next.splice(overIndex, 0, removed);

			onSetViewModel([{ key: "hotelInfo", value: { ...hotelInfo, value: next } }]);
		},
		[hotelInfo, onSetViewModel],
	);

	return {
		hotelInfo,
		append,
		removeHotelInfoByIndex,
		reorderHotelInfo,
		updateTitle,
		updateDescription,
	};
};
